import React, { lazy } from 'react';
import { Navigate } from 'react-router-dom';

// Attendance consolidated into the shared /attendance workspace (routes/index.tsx);
// legacy paths redirect to its tabs.
const redirectTo = (to: string) => () => React.createElement(Navigate, { to, replace: true });

const SalaryRevisionList = lazy(() => import('../../pages/SalaryRevision/SalaryRevisionList'));
const SalaryRevisionDetail = lazy(() => import('../../pages/SalaryRevision/SalaryRevisionDetail'));
const MeritMatrix = lazy(() => import('../../pages/SalaryRevision/MeritMatrix'));

const AdminDashboard = lazy(() => import('../../pages/Dashboard/Admin'));
// const NotFound = lazy(() => import('../../NotFound'));

const Users = lazy(() => import('../../pages/UserManagement/Users'));
const AllEmployeesDetail = lazy(() => import('../../pages/Employees/AllEmployeesDetail'));
const Holidays = lazy(() => import('../../pages/Leaves & Holidays/holidays'));
const OrganizationManagement = lazy(() => import('../../pages/OrganizationManagement/OrganizationManagement'));
const Departments = lazy(() => import('../../pages/Departments/Departments'));
const Designations = lazy(() => import('../../pages/Designations/Designation'));
const Permissions = lazy(() => import('../../pages/Roles & Permissions/Permissions'));
const PermissionSets = lazy(() => import('../../pages/Roles & Permissions/PermissionSets'));
const AssignPermissions = lazy(() => import('../../pages/Roles & Permissions/PermissionAssign'));
const Roles = lazy(() => import('../../pages/Roles & Permissions/Roles'));
const AttendanceSheet = lazy(() => import('../../pages/Employees/AttendanceSheet'));
const AttendanceReport = lazy(() => import('../../pages/Employees/AttendanceReport'));
const AttendanceImport = lazy(() => import('../../pages/Employees/AttendanceImport'));
const LeaveTypes = lazy(() => import('../../pages/Leaves & Holidays/leaveTypes'));
const Shifts = lazy(() => import('../../pages/Shifts/Shifts'));
const Locations = lazy(() => import('../../pages/Locations/Locations'));
const HrDocuments = lazy(() => import('../../pages/HrDocuments/HrDocuments'));
const Assets = lazy(() => import('../../pages/Assests/Assets'));
const AttendanceRules = lazy(() => import('../../pages/AttendanceRules/AttendanceRules'));
const FinalSettlementRules = lazy(() => import('../../pages/FinalSettlementRules/FinalSettlementRules'));
const Insurance = lazy(() => import('../../pages/Insurance/Insurance'));
const KpiDashboard = lazy(() => import('../../pages/Kpis/KpiDashboard'));
const KpiRules = lazy(() => import('../../pages/Kpis/KpiRules'));
const KpiSlabs = lazy(() => import('../../pages/Kpis/KpiSlabs'));
const KpiEntries = lazy(() => import('../../pages/Kpis/KpiEntries'));
const UploadAchievements = lazy(() => import('../../pages/Kpis/UploadAchievements'));
const KpiAssignments = lazy(() => import('../../pages/Kpis/KpiAssignments'));
const EmployeeScorecard = lazy(() => import('../../pages/Kpis/EmployeeScorecard'));
const BonusManagement = lazy(() => import('../../pages/Kpis/BonusManagement'));
const OvertimeRates = lazy(() => import('../../pages/Overtime/OvertimeRate'));
const PulseSurveys = lazy(() => import('../../pages/PusleServeys/PulseSurveys'));
const SelfEvaluations = lazy(() => import('../../pages/SelfEvaluations/SelfEvaluation'));

const AllEmployeeResponses = lazy(() => import('../../pages/PusleServeys/AllEmployeeResponses'));
const AllEmployeesSelfEvaluationResponses = lazy(() => import('../../pages/SelfEvaluations/EmployeesSelfEvaluationResponses'));
const SmtpSettings = lazy(() => import('../../pages/OrganizationManagement/SmtpSettings/SmtpSettings'));
const SmtpOAuthCallback = lazy(() => import('../../pages/OrganizationManagement/SmtpSettings/SmtpOAuthCallback'));
const EmailProviders = lazy(() => import('../../pages/EmailProviders/EmailProviders'));
const AttendanceAnalyticsPage = lazy(() => import('../../pages/Analytics/AttendanceAnalytics'));
const TraitsManagement = lazy(() => import('../../pages/Traits/TraitsManagement'));
const EmployeeTraitsManagement = lazy(() => import('../../pages/EmployeeTraits/EmployeeTraitsManagement'));

const IssueTicketsPage = lazy(() => import('../../pages/IssueTickets/IssueTicketsPage'));
const WarningsPage = lazy(() => import('../../pages/Warnings/Warnings'));
const WarningRulesPage = lazy(() => import('../../pages/WarningRules/WarningRules'));
const WarningTypesPage = lazy(() => import('../../pages/WarningTypes/WarningTypes'));
const PoliciesPage = lazy(() => import('../../pages/Policies/Policies'));

// Appraisal Management
const AppraisalCycles = lazy(() => import('../../pages/AppraisalsManagement/AppraisalCycle/AppraisalCycle'));

const AppraisalTemplates = lazy(() => import('../../pages/AppraisalsManagement/AppraisalTemplate/AppraisalTemplate'));
const Appraisals = lazy(() => import('../../pages/AppraisalsManagement/Appraisals/Appraisals'));
const AppraisalDetail = lazy(() => import('../../pages/AppraisalsManagement/Appraisals/AppraisalDetail'));
const ManagerPendingAppraisals = lazy(() => import('../../pages/AppraisalsManagement/Appraisals/ManagerPendingAppraisals'));
const HRPendingAppraisals = lazy(() => import('../../pages/AppraisalsManagement/Appraisals/HRPendingAppraisals'));
const CycleTemplateMapping = lazy(() => import('../../pages/AppraisalsManagement/AppraisalCycle/CycleTemplateMapping'));
const BulkAssign = lazy(() => import('../../pages/AppraisalsManagement/Appraisals/BulkAssign'));
const PendingApprovals = lazy(() => import('../../pages/Approvals/PendingApprovals'));
const HrAttendanceRequests = lazy(() => import('../../pages/AttendanceRegularization/HrAttendanceRequests'));
const AdminEmployeeDocuments = lazy(() => import('../../pages/EmployeeDocuments/EmployeeDocuments'));
const AdminExpiringDocuments = lazy(() => import('../../pages/EmployeeDocuments/ExpiringDocuments'));
const AdminMissingDocuments = lazy(() => import('../../pages/EmployeeDocuments/MissingDocuments'));

// Provident Fund
const ProvidentFundDashboard = lazy(() => import('../../pages/ProvidentFund/ProvidentFundDashboard'));
const EmployeePFView = lazy(() => import('../../pages/ProvidentFund/EmployeePFView'));
const PayrollRuns = lazy(() => import('../../pages/HrPayroll/PayrollRuns'));
const PayrollRunDetail = lazy(() => import('../../pages/HrPayroll/PayrollRunDetail'));
const Payslips = lazy(() => import('../../pages/HrPayroll/Payslips'));
const PayslipTemplates = lazy(() => import('../../pages/HrPayroll/PayslipTemplates'));

// Platform Administration
const SetupCenter = lazy(() => import('../../pages/SetupCenter/SetupCenter'));
const SubscriptionPage = lazy(() => import('../../pages/Subscription/SubscriptionPage'));


const adminRoutesConfig = [

  { path: '/v1/admin/dashboard', component: AdminDashboard },
  { path: '/v1/users', component: Users },
  { path: '/v1/employees', component: AllEmployeesDetail },
  { path: '/v1/holidays', component: Holidays },
  { path: '/v1/organization-management', component: OrganizationManagement },
  { path: '/v1/location-management', component: Locations },
  { path: '/v1/department-management', component: Departments },
  { path: '/v1/designation-management', component: Designations },
  // Legacy attendance paths → shared /attendance workspace (defined in routes/index.tsx)
  { path: '/v1/attendance-management', component: redirectTo('/attendance?tab=team') },
  { path: '/v1/attendance-report', component: AttendanceReport },
  { path: '/v1/attendance-import', component: AttendanceImport },
  { path: '/v1/leaves-types', component: redirectTo('/attendance?tab=settings') },
  { path: '/v1/shift-management', component: Shifts },
  { path: '/v1/hr-documents-management', component: HrDocuments },
  { path: '/v1/assets-management', component: Assets },
  { path: '/v1/attendance-rules-management', component: redirectTo('/attendance?tab=settings') },
  { path: '/v1/final-settlement-rules-management', component: FinalSettlementRules },
  { path: '/v1/insurance-management', component: Insurance },
  { path: '/v1/payslips-management', component: Payslips },
  { path: '/v1/payslip-templates', component: PayslipTemplates },
  { path: '/v1/kpi-dashboard', component: KpiDashboard },
  { path: '/v1/kpi-rules', component: KpiRules },
  { path: '/v1/kpi-slabs', component: KpiSlabs },
  { path: '/v1/kpi-entries', component: KpiEntries },
  { path: '/v1/kpi-achievements', component: UploadAchievements },
  { path: '/v1/kpi-assignments', component: KpiAssignments },
  { path: '/v1/kpi-scorecard', component: EmployeeScorecard },
  { path: '/v1/bonus-management', component: BonusManagement },
  { path: '/v1/overtime-rates', component: OvertimeRates },
  { path: '/v1/pulse-surveys', component: PulseSurveys },

  { path: '/v1/all-employee-responses', component: AllEmployeeResponses },
  { path: '/v1/self-evaluations', component: SelfEvaluations },
  { path: '/v1/self-evaluations/all-employee-responses', component: AllEmployeesSelfEvaluationResponses },

  { path: '/v1/attendance-analytics', component: redirectTo('/attendance?tab=insights') },

  { path: '/v1/payroll-runs', component: PayrollRuns },
  { path: '/v1/payroll-runs/:runId', component: PayrollRunDetail },


  { path: '/v1/traits-management', component: TraitsManagement },
  { path: '/v1/employee-traits-management', component: EmployeeTraitsManagement },


  { path: '/v1/issue-tickets-management', component: IssueTicketsPage },
  { path: '/v1/warnings', component: WarningsPage },
  { path: '/v1/warning-rules', component: WarningRulesPage },
  { path: '/v1/warning-types', component: WarningTypesPage },
  { path: '/v1/policies-management', component: PoliciesPage },

  { path: '/v1/smtp-settings', component: SmtpSettings },
  { path: '/v1/smtp/callback/azure', component: SmtpOAuthCallback },
  { path: '/v1/smtp/callback/google', component: SmtpOAuthCallback },
  { path: '/v1/email-providers', component: EmailProviders },

  // Permissions Routes
  { path: '/v1/permissions', component: Permissions },
  { path: '/v1/permission-sets', component: PermissionSets },
  { path: '/v1/assign-permissions', component: AssignPermissions },
  { path: '/v1/roles', component: Roles },

  // Appraisals Management (Admin gets full HR-level access)
  { path: '/v1/hr/appraisals-cycle', component: AppraisalCycles },
  { path: '/v1/hr/appraisals-templates', component: AppraisalTemplates },
  { path: '/v1/hr/appraisals', component: Appraisals },
  { path: '/v1/hr/appraisals/pending/manager', component: ManagerPendingAppraisals },
  { path: '/v1/hr/appraisals/pending/hr', component: HRPendingAppraisals },
  { path: '/v1/hr/appraisals/:id', component: AppraisalDetail },
  { path: '/v1/hr/appraisals-cycle/:cycleId/template-mapping', component: CycleTemplateMapping },
  { path: '/v1/hr/appraisals-cycle/:cycleId/bulk-assign', component: BulkAssign },

  // Approval Engine
  { path: '/v1/pending-approvals', component: PendingApprovals },

  // Attendance Regularization → workspace Approvals tab
  { path: '/v1/attendance-regularization', component: redirectTo('/attendance?tab=approvals') },

  // Employee Document Management
  { path: '/v1/employee-documents', component: AdminEmployeeDocuments },
  { path: '/v1/employee-documents/expiring', component: AdminExpiringDocuments },
  { path: '/v1/employee-documents/missing', component: AdminMissingDocuments },

  // Provident Fund
  { path: '/v1/provident-fund', component: ProvidentFundDashboard },
  { path: '/v1/provident-fund/employee/:id', component: EmployeePFView },

  // Salary Revision / Merit (Admin gets full HR-level access at /v1/hr/* paths)
  { path: '/v1/hr/salary-revisions', component: SalaryRevisionList },
  { path: '/v1/hr/merit-matrix', component: MeritMatrix },
  { path: '/v1/hr/salary-revisions/:id', component: SalaryRevisionDetail },

  // Platform Administration
  { path: '/v1/admin/setup-center', component: SetupCenter },
  { path: '/v1/admin/subscription', component: SubscriptionPage },

  // { path: '/v1/admin/*', component: NotFound },
];

export default adminRoutesConfig;
